// Shared time/date formatters — always render in US 12-hour format (hh:mm AM/PM).
// Storage remains 24h "HH:MM" in DB; these helpers only affect display.

/**
 * Convert "HH:MM" (24h) → "hh:mm AM/PM".
 * Returns empty string for falsy/invalid input.
 */
export function formatTime12h(raw) {
  if (!raw || typeof raw !== "string") return "";
  const m = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return raw;
  let h = parseInt(m[1], 10);
  const min = m[2];
  if (isNaN(h) || h < 0 || h > 23) return raw;
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${String(h).padStart(2, "0")}:${min} ${suffix}`;
}

/** Convert back to 24h "HH:MM" from pieces (h12, m, meridiem). */
export function to24h(hour12, minute, meridiem) {
  let h = parseInt(hour12, 10);
  let m = parseInt(minute, 10);
  if (isNaN(h)) h = 0;
  if (isNaN(m)) m = 0;
  h = Math.max(1, Math.min(12, h));
  m = Math.max(0, Math.min(59, m));
  if (meridiem === "PM" && h < 12) h += 12;
  if (meridiem === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Split "HH:MM" (24h) into 12h parts { hour12, minute, meridiem } for inputs. */
export function split12h(raw) {
  if (!raw) return { hour12: "", minute: "", meridiem: "AM" };
  const m = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return { hour12: "", minute: "", meridiem: "AM" };
  let h = parseInt(m[1], 10);
  const min = m[2];
  if (isNaN(h)) return { hour12: "", minute: "", meridiem: "AM" };
  const meridiem = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return { hour12: String(h), minute: min, meridiem };
}

/** ISO datetime string → "Apr 23, 2026, 02:30 PM". */
export function formatDateTime12h(iso, opts = {}) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
      ...opts,
    });
  } catch (_e) { return ""; }
}

/** Short activity log date: "Apr 23, 02:30 PM". */
export function formatLogTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch (_e) { return ""; }
}

/** Long scheduling label: "April 23, 2026 - 02:30 PM". */
export function formatSchedule12h(date, time) {
  if (!date) return "";
  try {
    const d = new Date(`${date}T${time || "00:00"}:00`);
    if (isNaN(d.getTime())) return "";
    const datePart = d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    if (!time) return datePart;
    return `${datePart} - ${formatTime12h(time)}`;
  } catch (_e) { return ""; }
}

/** Time-only locale formatter from ISO string. */
export function formatClockFromIso(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  } catch (_e) { return ""; }
}
